import { useAction, useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { match } from 'ts-pattern'

import { AsyncStateN } from '@blade/std-lib'
import { userAtom } from '@root/entities/auth'
import {
  currentUserIsRefreshingAtom,
  otherUserDelugeIdAtom,
  otherUserIsRefreshingAtom,
  profileStateAtom,
  refreshProfileAction,
  refreshUserProfileAction,
  userProfileStateAtom,
} from '@root/entities/profile'
import { ProfileCoins } from '@root/features/profile/profile-coins'
import { RectangleNetworkBackground } from '@root/shared/ui'
import { InfoMessage } from '@root/shared/ui-kit'

import { ProfileOwner } from '../../features/user/profile/model/profile-owner'

import { ProfileBoardComments } from './board/profile-board-comments'
import { ProfileBoardStatistics } from './board/profile-board-statistics'
import { ReferralBoard } from './board/referral-board/referral-board'
import { ProfileBoardTransactions } from './board/transactions-board/transactions-board'
import { UserConnectionsBoardFollowers } from './board/user-connections-board/user-connections-board-followers'
import { UserConnectionsBoardFollowing } from './board/user-connections-board/user-connections-board-following'
import { ProfileBoardContentSkeleton } from './profile-board-content-skeleton'

import type { ProfileBoardContentTab } from './profile-board-content-skeleton'
import type { Profile } from '@root/entities/profile'
import type { DelugeId } from '@root/entities/user-short'

type ProfileBoardContentOwner = 'current-user' | 'other-user'

type ProfileBoardContentProps = {
  owner?: ProfileBoardContentOwner
  tab: ProfileBoardContentTab
  delugeId: DelugeId
}

const PROFILE_OWNER_MAP: Record<ProfileBoardContentOwner, ProfileOwner> = {
  'current-user': ProfileOwner.CurrentUser,
  'other-user': ProfileOwner.AnotherUsers,
}

export const ProfileBoardContent = ({
  owner = 'current-user',
  tab,
  delugeId,
}: ProfileBoardContentProps) => {
  const { t } = useTranslation()
  const refreshProfile = useAction(refreshProfileAction)
  const refreshUserProfile = useAction(refreshUserProfileAction)
  const [otherUserDelugeId] = useAtom(otherUserDelugeIdAtom)
  const [currentProfileState] = useAtom(profileStateAtom)
  const [otherProfileState] = useAtom(userProfileStateAtom)
  const [currentIsRefreshing] = useAtom(currentUserIsRefreshingAtom)
  const [otherIsRefreshing] = useAtom(otherUserIsRefreshingAtom)
  // How many follow this account, straight off the signed-in user, so the
  // followers list can tell "nobody yet" from "not loaded".
  const [followersCount] = useAtom(
    (ctx) => ctx.spy(userAtom)?.followers.length ?? 0,
    [],
  )

  const profileState =
    owner === 'other-user'
      ? (otherProfileState ?? AsyncStateN.loading())
      : currentProfileState
  const isRefreshing =
    owner === 'other-user' ? otherIsRefreshing : currentIsRefreshing

  const handleRetry = () => {
    if (owner === 'other-user') {
      if (otherUserDelugeId) {
        refreshUserProfile(otherUserDelugeId)
      }
      return
    }

    refreshProfile()
  }

  const renderScreen = ({
    tab,
    delugeId,
    owner,
  }: {
    tab: ProfileBoardContentTab
    delugeId: DelugeId
    profile: Profile.Profile
    owner: ProfileBoardContentOwner
  }) => {
    const profileOwner = PROFILE_OWNER_MAP[owner]

    return match(tab)
      .with('transactions', () => (
        <ProfileBoardTransactions
          delugeId={owner === 'other-user' ? delugeId : undefined}
          profileOwner={profileOwner}
        />
      ))
      .with('statistics', () => (
        <ProfileBoardStatistics owner={profileOwner} delugeId={delugeId} />
      ))
      .with('coins', () => <ProfileCoins delugeId={delugeId} />)
      .with('followers', () => (
        <UserConnectionsBoardFollowers
          delugeId={delugeId}
          followersCount={followersCount}
        />
      ))
      .with('following', () => (
        <UserConnectionsBoardFollowing delugeId={delugeId} />
      ))
      .with('comments', () => <ProfileBoardComments delugeId={delugeId} />)
      .with('referral', () => <ReferralBoard />)
      .exhaustive()
  }

  // These tabs stand on their own. The statistics read the account's wallets
  // and ask the wallet-profile endpoint about them; the two lists need an id
  // and a count, both of which arrive with the signed-in user; the posts need
  // the id and nothing else. None of them has any business being hidden
  // behind a profile request that is not ported yet — which is exactly what
  // they were: an error message where the figures, the people and the posts
  // belong.
  const standsAlone =
    tab === 'statistics' ||
    tab === 'followers' ||
    tab === 'following' ||
    tab === 'comments'

  if (standsAlone && delugeId) {
    return renderScreen({
      tab,
      delugeId,
      profile: undefined as never,
      owner,
    })
  }

  return AsyncStateN.matchWaiting(profileState, {
    Waiting: () => (
      <ProfileBoardContentSkeleton tab={tab} delugeId={delugeId} />
    ),
    Error: () =>
      isRefreshing ? (
        <ProfileBoardContentSkeleton tab={tab} delugeId={delugeId} />
      ) : (
        <Root>
          <Container>
            <InfoMessage
              intent="error"
              variant="dt"
              onRetry={handleRetry}
              title={t('common.error')}
              description={t('common.something-went-wrong')}
            />
          </Container>
        </Root>
      ),
    Success: (profile) =>
      delugeId ? (
        renderScreen({ tab, delugeId, profile, owner })
      ) : (
        <ProfileBoardContentSkeleton tab={tab} delugeId={delugeId} />
      ),
  })
}

const Root = styled.div`
  ${RectangleNetworkBackground}

  display: grid;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`
const Container = styled.div``
